import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Header from '../../layouts/Header';
import { Container, Spinner, Form } from 'react-bootstrap';
import axiosInstance from '../../api/axios';
import OutletSelect from '../../components/OutletSelect';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import IntendImages from '../../assets/img/indentImg.png';

/* ─── constants ──────── */
const STATUS_CONFIG = {
    PENDING: { label: 'Pending', color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
    COMPLETED: { label: 'Dispatched', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    RECEIVED: { label: 'Received', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
    CLOSED: { label: 'Closed', color: '#374151', bg: '#f3f4f6', border: '#9ca3af' },
};

const UPDATABLE_STATUSES = {
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};

const fmt = (iso) =>
    new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

/* ─── Helper function to format date for input fields ──────── */
const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/* ─── Helper function to get first day of current month ──────── */
const getFirstDayOfMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
};

/* ─── PDF Download Function ──────────────────────────────── */
const handleDownloadPDF = (request) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];
    const upperStatus = (request.status || 'PENDING').toUpperCase();

    // ── Header background ──
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    // Logo
    try {
        doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20);
    } catch (e) { }

    // Header text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('STOCK REQUEST', pageWidth - 14, 13, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${request.indentNumber || ''}`, pageWidth - 14, 20, { align: 'right' });

    doc.setTextColor(40, 40, 40);

    /* -------- BASIC INFO -------- */
    const startY = 36;
    const col1Label = 14;
    const col1Value = 38;
    const col2Label = 110;
    const col2Value = 128;

    doc.setFontSize(8.5);

    // ── Left side: Indent details ──
    doc.setFont('helvetica', 'normal');
    doc.text('Indent :', col1Label, startY);
    doc.setFont('helvetica', 'bold');
    doc.text(request.indentNumber || '-', col1Value, startY);

    doc.setFont('helvetica', 'normal');
    doc.text('Status :', col1Label, startY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(upperStatus, col1Value, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.text('Date :', col1Label, startY + 12);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(request.createdAt), col1Value, startY + 12);

    doc.setFont('helvetica', 'normal');
    doc.text('Fin. Year :', col1Label, startY + 18);
    doc.setFont('helvetica', 'bold');
    doc.text(request.fin_year || '-', col1Value, startY + 18);

    // ── Right side: Outlet details ──
    doc.setFont('helvetica', 'normal');
    doc.text('Outlet :', col2Label, startY);
    doc.setFont('helvetica', 'bold');
    doc.text(request?.outlet?.name || '-', col2Value, startY);

    doc.setFont('helvetica', 'normal');
    doc.text('City :', col2Label, startY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(request?.outlet?.city || '-', col2Value, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.text('Address :', col2Label, startY + 12);
    doc.setFont('helvetica', 'bold');
    const address = request?.outlet?.address || '-';
    const addressLines = doc.splitTextToSize(address, 60);
    doc.text(addressLines, col2Value, startY + 12);

    // ── Divider line ──
    const dividerY = startY + 28;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, dividerY, pageWidth - 14, dividerY);

    /* -------- TABLE -------- */
    const items = request.items || [];
    let totalRequested = 0;
    const showReceivedQty = request.status === 'RECEIVED' || request.status === 'CLOSED';

    const sortedItems = [...items].sort((a, b) => {
        const mainA = a.rawMaterial?.mainCategory?.name || '';
        const mainB = b.rawMaterial?.mainCategory?.name || '';
        const mainCompare = mainA.localeCompare(mainB);
        if (mainCompare !== 0) return mainCompare;
        const subA = a.rawMaterial?.subCategory?.name || '';
        const subB = b.rawMaterial?.subCategory?.name || '';
        return subA.localeCompare(subB);
    });

    const tableRows = sortedItems.map((it, index) => {
        const rm = it.rawMaterial || {};
        const requested = Number(it.requestedQuantity || 0);
        const received = Number(it.receivedQuantity || 0);
        totalRequested += requested;

        return [
            index + 1,
            rm.name || '-',
            rm.mainCategory?.name || '-',
            rm.subCategory?.name || '-',
            rm.unit || '-',
            requested.toFixed(2),
            showReceivedQty ? received.toFixed(2) : '-',
        ];
    });

    autoTable(doc, {
        startY: dividerY + 6,
        head: [['Sr No', 'Material', 'Main Cat', 'Sub Cat', 'Unit', 'Req Qty', 'Rec Qty']],
        body: tableRows,
        theme: 'grid',
        headStyles: {
            fillColor: primaryColor,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 8,
        },
        styles: {
            fontSize: 8,
            cellPadding: 2.5,
            font: 'helvetica',
            valign: 'middle',
        },
        alternateRowStyles: {
            fillColor: [245, 250, 255],
        },
        columnStyles: {
            0: { halign: 'center', cellWidth: 10 },
            1: { halign: 'left' },
            2: { halign: 'center', cellWidth: 28 },
            3: { halign: 'center', cellWidth: 28 },
            4: { halign: 'center', cellWidth: 16 },
            5: { halign: 'center', cellWidth: 18 },
            6: { halign: 'center', cellWidth: 18 },
        },
    });

    /* -------- TOTALS -------- */
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(
        `Total Items: ${items.length}   |   Total Requested: ${totalRequested.toFixed(2)}`,
        pageWidth - 14,
        finalY,
        { align: 'right' }
    );

    /* -------- SIGNATURE -------- */
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
    doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

    /* -------- FOOTER -------- */
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(
        `Generated on ${new Date().toLocaleString('en-IN')}`,
        14,
        doc.internal.pageSize.getHeight() - 10
    );

    doc.save(`Stock-Request-${request.indentNumber || 'Details'}.pdf`);
};

/* ─── Combined PDF (PENDING + ACCEPTED aggregated) ──────── */
const handleDownloadCombinedPDF = (allRequests) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];

    // ── Header background ──
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    // Logo
    try { doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20); } catch (e) { }

    // Header text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Cummulative Stock Request', (pageWidth / 2) + 20, 16, { align: 'center' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    doc.setTextColor(40, 40, 40);

    // ── Aggregate materials across PENDING + ACCEPTED ──
    const materialMap = {};

    allRequests.forEach(req => {
        (req.items || []).forEach(item => {
            const rm = item.rawMaterial || {};
            const id = rm._id || rm.name || 'unknown';
            if (!materialMap[id]) {
                materialMap[id] = {
                    name: rm.name || '—',
                    mainCat: rm.mainCategory?.name || '—',
                    subCat: rm.subCategory?.name || '—',
                    unit: rm.unit || '—',
                    hsn: rm.hsn || '—',
                    totalQty: 0,
                };
            }
            const qty = Number(item.requestedQuantity || 0);
            materialMap[id].totalQty += qty;
        });
    });

    const rows = Object.values(materialMap)
        .sort((a, b) => {
            const catCompare = a.mainCat.localeCompare(b.mainCat);
            if (catCompare !== 0) return catCompare;
            return a.subCat.localeCompare(b.subCat);
        })
        .map((m, idx) => [
            idx + 1,
            m.name,
            `${m.mainCat} > ${m.subCat}`,
            Number(m.totalQty).toFixed(2),
            m.unit,
        ]);

    let totalQuantity = 0;
    Object.values(materialMap).forEach(m => { totalQuantity += m.totalQty; });

    autoTable(doc, {
        startY: 34,
        head: [['Sr No', 'Material', 'Category', 'Req. Qty', 'Unit']],
        body: rows,
        theme: 'grid',
        headStyles: {
            fillColor: primaryColor,
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
        },
        styles: { fontSize: 8, cellPadding: 2, font: 'helvetica' },
        alternateRowStyles: { fillColor: [245, 250, 255] },
        columnStyles: {
            0: { halign: 'center', cellWidth: 10 },
            1: { halign: 'left', cellWidth: 60 },
            2: { halign: 'left', cellWidth: 60 },
            3: { halign: 'center', cellWidth: 25 },
            4: { halign: 'center', cellWidth: 25 },
        },
    });

    // ── Totals ──
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(
        `Materials: ${rows.length}`,
        pageWidth - 14, finalY, { align: 'right' }
    );

    // ── Signature ──
    doc.setFont('helvetica', 'normal');
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
    doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

    // ── Footer ──
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

    doc.save(`Cumulative-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`);
};

/* ─── StatusPill ─────────────────────────────────────────── */
const StatusPill = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' };
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
            whiteSpace: 'nowrap',
        }}>
            {cfg.label}
        </span>
    );
};

/* ─── Items Expandable ───────────────────────────────────── */
const ItemsRow = ({ items, status }) => {
    const [open, setOpen] = useState(false);

    return (
        <div style={{ marginTop: 10 }}>
            <button onClick={() => setOpen(o => !o)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#6366f1', fontSize: 13, fontWeight: 600,
            }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: 6,
                    background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 800,
                }}>
                    {items.length}
                </span>
                Item{items.length !== 1 ? 's' : ''}
                <span style={{
                    fontSize: 9, display: 'inline-block',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s',
                }}>▼</span>
            </button>

            {open && (
                <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: status === 'RECEIVED' ? '2.5fr 1fr 1fr 1fr 140px 140px 140px' : '2.5fr 1fr 1fr 1fr 140px',
                        background: '#f9fafb', padding: '7px 14px', borderBottom: '1px solid #e5e7eb',
                        fontSize: 11, fontWeight: 700, color: '#6b7280',
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>
                        <span>Material</span><span>Category</span><span>HSN</span><span>Unit</span>
                        <span>Requested Qty</span>
                        {status === 'RECEIVED' && <span>Received Qty</span>}
                        {status === 'RECEIVED' && <span>Balance Qty</span>}
                    </div>
                    {items.map((item, idx) => {
                        const rm = item.rawMaterial || {};
                        return (
                            <div key={item._id || idx} style={{
                                display: 'grid',
                                gridTemplateColumns: status === 'RECEIVED' ? '2.5fr 1fr 1fr 1fr 130px 130px 130px' : '2.5fr 1fr 1fr 1fr 130px',
                                padding: '9px 14px', alignItems: 'center',
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                                fontSize: 13,
                            }}>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>{rm.name || '—'}</div>
                                    {rm.description && (
                                        <div style={{
                                            color: '#9ca3af', fontSize: 11, marginTop: 1,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300,
                                        }}>{rm.description}</div>
                                    )}
                                </div>
                                <span style={{ color: '#373b41', fontSize: 12 }}>{rm?.subCategory?.name} <br />
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>{rm?.mainCategory?.name || '—'}</div>
                                </span>
                                <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{rm?.hsn || '—'}</span>
                                <span style={{ color: '#6b7280', fontSize: 12 }}>{rm?.unit || '—'}</span>
                                <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                    {item?.requestedQuantity?.toFixed(2)} <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{rm?.unit || ''}</span>
                                </span>
                                {status === 'RECEIVED' && (
                                    <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                        {item?.receivedQuantity?.toFixed(2)} <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{rm?.unit || ''}</span>
                                    </span>
                                )}
                                {status === 'RECEIVED' && (
                                    <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                        {(item?.requestedQuantity - item?.receivedQuantity)?.toFixed(2)} <span style={{ fontSize: 12, color: '#6366f1', fontWeight: 500 }}>{rm?.unit || ''}</span>
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ─── Update Modal ───────────────────────────────────────── */
const UpdateModal = ({ request, onHide, onUpdated, accessToken }) => {
    const [status, setStatus] = useState('ACCEPTED');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const alreadyUpdated = request?.status === 'ACCEPTED' || request?.status === 'REJECTED';

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            await axiosInstance.put(`stock-requisition/update-status`, { _id: request._id, status }, { headers });
            onUpdated(request._id, status);
            onHide();
        } catch (error) {
            console.log("Error While Updating :", error);

            if (error?.response) {
                let errorMessage = '';
                if (error.response?.status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error
                } else if (error.response?.status === 400) {
                    errorMessage = error?.response?.data?.message || error?.response?.data?.error;
                } else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error || error?.response?.data?.message
                }
                setError(errorMessage || 'Failed to update. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1050,
            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onHide}>
            <div style={{
                background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
                margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>

                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', borderBottom: '1px solid #f3f4f6',
                }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>Update Stock Request</span>
                    <button onClick={onHide} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0,
                    }}>×</button>
                </div>

                <div style={{ padding: '20px' }}>

                    <div style={{
                        marginBottom: 16, padding: '10px 14px',
                        background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Indent Number
                            </div>
                            <div style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace', fontSize: 14 }}>
                                {request.indentNumber}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Current Status
                            </div>
                            <StatusPill status={request.status} />
                        </div>
                    </div>

                    {alreadyUpdated ? (
                        <div>
                            <div style={{
                                padding: '16px', borderRadius: 9,
                                background: '#f9fafb', border: '1px solid #e5e7eb',
                                textAlign: 'center', color: '#6b7280', fontSize: 13, lineHeight: 1.6,
                            }}>
                                This request has already been{' '}
                                <strong style={{ color: '#374151' }}>{request.status.toLowerCase()}</strong>{' '}
                                .If you want to change the status, you can change it
                            </div>

                            <div className='mt-3' style={{ display: 'flex', gap: 10 }}>
                                {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
                                    <label key={key} style={{
                                        flex: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 8,
                                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                                        border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`,
                                        background: status === key ? cfg.bg : '#fff',
                                        transition: 'all .15s', textAlign: 'center',
                                    }}>
                                        <input
                                            type="radio" name="status" value={key}
                                            checked={status === key}
                                            onChange={() => setStatus(key)}
                                            style={{ accentColor: cfg.color, width: 15, height: 15 }}
                                        />
                                        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>
                                            {cfg.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                Set Status{' '}
                                <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(choose one)</span>
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
                                    <label key={key} style={{
                                        flex: 1, display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 8,
                                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                                        border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`,
                                        background: status === key ? cfg.bg : '#fff',
                                        transition: 'all .15s', textAlign: 'center',
                                    }}>
                                        <input
                                            type="radio" name="status" value={key}
                                            checked={status === key}
                                            onChange={() => setStatus(key)}
                                            style={{ accentColor: cfg.color, width: 15, height: 15 }}
                                        />
                                        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>
                                            {cfg.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {error && (
                        <div style={{
                            marginTop: 12, padding: '10px 14px',
                            background: '#fee2e2', border: '1px solid #fca5a5',
                            borderRadius: 8, color: '#dc2626', fontSize: 13,
                        }}>{error}</div>
                    )}
                </div>

                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #f3f4f6',
                    display: 'flex', justifyContent: 'flex-end', gap: 8,
                }}>
                    <button onClick={onHide} style={{
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        padding: '8px 18px', fontSize: 13, fontWeight: 600,
                        color: '#374151', cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                        {alreadyUpdated ? 'Close' : 'Cancel'}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            background: saving ? '#a5b4fc' : '#6366f1',
                            border: 'none', borderRadius: 8, padding: '8px 22px',
                            fontSize: 13, fontWeight: 700, color: '#fff',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                        }}>
                        {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Request Card ───────────────────────────────────────── */
const RequestCard = ({ req, onUpdate, onDownloadPDF, isDark }) => {
    const cardBg = isDark ? '#1a2132' : '#fff';
    const border = isDark ? '#2d3748' : '#e5e7eb';
    const text = isDark ? '#f3f4f6' : '#111827';
    const muted = isDark ? '#9ca3af' : '#6b7280';

    const isAccepted = req.status === 'ACCEPTED';

    return (
        <div style={{
            background: cardBg, border: `1px solid ${border}`, borderRadius: 10,
            padding: '16px 18px', marginBottom: 12,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'box-shadow .2s',
        }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = isDark ? '0 4px 18px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.09)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)'}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#6366f1' }}>
                            {req.indentNumber}
                        </span>
                        <StatusPill status={req.status === 'COMPELETED' ? 'DISPATCHED' : req.status  } />
                        <span style={{
                            fontSize: 11, color: muted,
                            background: isDark ? '#252d3d' : '#f3f4f6',
                            padding: '2px 8px', borderRadius: 6, fontWeight: 600,
                        }}>
                            FY {req.fin_year}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                        Outlet:{' '}
                        <span style={{ color: text, fontWeight: 500 }}>
                            {req?.outlet?.name || req?.outlet?._id || '—'}, {req?.outlet?.city}
                        </span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ textAlign: 'right', fontSize: 12, color: muted }}>
                        <div>Raised on: <span style={{ color: text }}>{fmt(req.createdAt)}</span></div>
                    </div>

                    {req.status !== 'COMPLETED' && req.status !== 'RECEIVED' && (
                        <button onClick={() => onUpdate(req)} style={{
                            background: isAccepted ? '#d1fae5' : '#f5f3ff',
                            border: `1px solid ${isAccepted ? '#6ee7b7' : '#ddd6fe'}`,
                            borderRadius: 7, padding: '6px 13px',
                            fontSize: 12, fontWeight: 700,
                            color: isAccepted ? '#059669' : '#7c3aed',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: 5,
                            fontFamily: 'inherit', transition: 'background .15s',
                        }}
                            onMouseEnter={e => e.currentTarget.style.background = isAccepted ? '#a7f3d0' : '#ede9fe'}
                            onMouseLeave={e => e.currentTarget.style.background = isAccepted ? '#d1fae5' : '#f5f3ff'}
                        >
                            {isAccepted ? '📦 Transfer Stock' : '✎ Update'}
                        </button>
                    )}

                    <button onClick={() => onDownloadPDF(req)} style={{
                        background: '#fef3c7',
                        border: '1px solid #fcd34d',
                        borderRadius: 7, padding: '6px 13px',
                        fontSize: 12, fontWeight: 700,
                        color: '#d97706',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                        display: 'flex', alignItems: 'center', gap: 5,
                        fontFamily: 'inherit', transition: 'background .15s',
                    }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}
                        title="Download PDF"
                    >
                        📄 PDF
                    </button>
                </div>
            </div>

            <ItemsRow items={req.items || []} status={req.status} />
        </div>
    );
};

/* ─── Pagination Component ───────────────────────────────────── */
const Pagination = ({ currentPage, totalPages, onPageChange, isDark }) => {
    const borderCol = isDark ? '#2d3748' : '#e5e7eb';
    const textMain = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, 4, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 8,
            marginTop: 20,
            flexWrap: 'wrap'
        }}>
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${borderCol}`,
                    borderRadius: 7,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: currentPage === 1 ? textMuted : textMain,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontFamily: 'inherit',
                }}
            >
                ← Previous
            </button>

            {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                    <span key={`ellipsis-${idx}`} style={{ color: textMuted, padding: '0 4px' }}>...</span>
                ) : (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        style={{
                            background: currentPage === page ? '#6366f1' : (isDark ? '#1e293b' : '#fff'),
                            border: `1px solid ${currentPage === page ? '#6366f1' : borderCol}`,
                            borderRadius: 7,
                            padding: '6px 12px',
                            fontSize: 13,
                            fontWeight: currentPage === page ? 700 : 600,
                            color: currentPage === page ? '#fff' : textMain,
                            cursor: 'pointer',
                            minWidth: 36,
                            fontFamily: 'inherit',
                        }}
                    >
                        {page}
                    </button>
                )
            ))}

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${borderCol}`,
                    borderRadius: 7,
                    padding: '6px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: currentPage === totalPages ? textMuted : textMain,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontFamily: 'inherit',
                }}
            >
                Next →
            </button>
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────── */
const StocksRequest = () => {

    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('authToken');

    const [stockRequests, setStockRequests] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [updateTarget, setUpdateTarget] = useState(null);

    // New state for pagination and date filters
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [statusCounts, setStatusCounts] = useState({});
    const [startDate, setStartDate] = useState(formatDateForInput(getFirstDayOfMonth()));
    const [endDate, setEndDate] = useState(formatDateForInput(new Date()));

    const navigate = useNavigate();

    const isDark = skin === 'dark';
    const pageBg = isDark ? '#111827' : '#f3f4f6';
    const cardBg = isDark ? '#1a2132' : '#fff';
    const textMain = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';
    const borderCol = isDark ? '#2d3748' : '#e5e7eb';
    const inputBg = isDark ? '#1e293b' : '#fff';

    /* ── fetch stock requests with pagination and filters ── */
    const getStockRequests = async (page = 1, filters = {}) => {
        setLoading(true);
        setError('');

        try {
            const headers = { Authorization: `Bearer ${accessToken}` };

            // Build query params
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', 20);

            // Add outlet filter if selected
            const outletId = filters.outlet?.value || filters.outlet?._id || selectedOutlet?.value || selectedOutlet?._id || '';
            if (outletId) {
                params.append('outlet', outletId);
            }

            // Add status filter (only if not 'ALL')
            const status = filters.status !== undefined ? filters.status : statusFilter;
            if (status && status !== 'ALL') {
                params.append('status', status);
            }

            // Add date filters
            const start = filters.startDate !== undefined ? filters.startDate : startDate;
            const end = filters.endDate !== undefined ? filters.endDate : endDate;
            if (start) {
                params.append('startDate', start);
            }
            if (end) {
                params.append('endDate', end);
            }

            const res = await axiosInstance.get(`stock-requisition?${params.toString()}`, { headers });

            console.log("Response of Stock Request", res?.data);

            const responseData = res?.data?.data || {};
            const requests = responseData.data || [];
            const pagination = responseData.pagination || {};
            const counts = responseData.statusCounts || {};

            // Sort by latest first
            const sortedData = requests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setStockRequests(sortedData);
            setCurrentPage(pagination.currentPage || 1);
            setTotalPages(pagination.totalPages || 1);
            setTotalRecords(pagination.totalRecords || 0);
            setStatusCounts(counts);

        } catch (err) {
            setStockRequests([]);
            console.error('Stock requests fetch error:', err);
            setError('Failed to load stock requests. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateClick = (req) => {
        if (req.status === 'ACCEPTED') {
            navigate('/purchase/stock-transfer', { state: { request: req } });
        } else {
            setUpdateTarget(req);
        }
    };

    // Initial load
    useEffect(() => {
        getStockRequests(1);
    }, []);

    const handleOutletChange = (option) => {
        setSelectedOutlet(option);
        setCurrentPage(1);
        getStockRequests(1, { outlet: option });
    };

    const handleStatusChange = (newStatus) => {
        setStatusFilter(newStatus);
        setCurrentPage(1);
        getStockRequests(1, { status: newStatus });
    };

    const handleDateChange = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
            setCurrentPage(1);
            getStockRequests(1, { startDate: value, endDate });
        } else {
            setEndDate(value);
            setCurrentPage(1);
            getStockRequests(1, { startDate, endDate: value });
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            getStockRequests(page);
            // Scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleUpdated = (id, newStatus) => {
        // Refresh the current page after update
        getStockRequests(currentPage);
    };

    // Calculate status counts including 'ALL'
    const getAllCount = () => {
        return Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
    };

    return (
        <React.Fragment>
            <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

            <Header onSkin={setSkin} />

            {updateTarget && (
                <UpdateModal
                    request={updateTarget}
                    onHide={() => setUpdateTarget(null)}
                    onUpdated={handleUpdated}
                    accessToken={accessToken}
                />
            )}

            <div className={`main main-app p-3 p-lg-3 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}
                style={{ minHeight: '100vh', background: pageBg, fontFamily: "'Nunito', 'Segoe UI', sans-serif" }}>
                <Container fluid>

                    {/* ── Page Header ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <h4 style={{ fontWeight: 800, fontSize: 21, margin: 0, color: textMain, letterSpacing: '-0.01em' }}>
                                Stock Requests
                            </h4>
                            <p style={{ color: textMuted, margin: '3px 0 0', fontSize: 13 }}>
                                Manage and track material requisitions from outlets
                            </p>
                        </div>
                        <button onClick={() => getStockRequests(currentPage)} disabled={loading} style={{
                            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 9,
                            padding: '9px 18px', fontWeight: 700, fontSize: 13,
                            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                        }}>
                            {loading
                                ? <><Spinner animation="border" size="sm" /> Refreshing…</>
                                : <><span style={{ fontSize: 15 }}>↺</span> Refresh</>}
                        </button>
                    </div>

                    {/* ── Filters Section ── */}
                    <div style={{
                        background: cardBg, border: `1px solid ${borderCol}`,
                        borderRadius: 10, padding: '16px', marginBottom: 14,
                    }}>
                        {/* First Row: Outlet + Date Filters */}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 0.8fr 0.8fr 1.5fr',
                            gap: 12,
                            marginBottom: 12,
                        }}>
                            {/* Outlet Select */}
                            <div>

                                <Form.Group controlId="outletSelect" style={{ margin: 0 }}>
                                    <OutletSelect
                                        value={selectedOutlet}
                                        onChange={handleOutletChange}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            {/* Start Date */}
                            <div >
                                <Form.Label style={{
                                    color: textMuted,
                                    marginBottom: 6,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    <i className="bi bi-calendar me-2"></i>Start Date
                                </Form.Label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleDateChange('start', e.target.value)}
                                    max={endDate}
                                    style={{
                                        width: '100%',
                                        background: isDark ? '#252d3d' : '#f9fafb',
                                        border: `1px solid ${borderCol}`,
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        fontSize: 13,
                                        color: textMain,
                                        fontFamily: 'inherit',
                                        outline: 'none',
                                    }}
                                />
                            </div>

                            {/* End Date */}
                            <div>
                                <Form.Label style={{
                                    color: textMuted,
                                    marginBottom: 6,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    <i className="bi bi-calendar me-2"></i>End Date
                                </Form.Label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => handleDateChange('end', e.target.value)}
                                    min={startDate}
                                    max={formatDateForInput(new Date())}
                                    style={{
                                        width: '100%',
                                        background: isDark ? '#252d3d' : '#f9fafb',
                                        border: `1px solid ${borderCol}`,
                                        borderRadius: 8,
                                        padding: '8px 12px',
                                        fontSize: 13,
                                        color: textMain,
                                        fontFamily: 'inherit',
                                        outline: 'none',
                                    }}
                                />
                            </div>
                            <div>
                                <Form.Label style={{
                                    color: textMuted,
                                    marginBottom: 6,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    <i className="bi bi-search me-2"></i>Search
                                </Form.Label>
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    background: isDark ? '#252d3d' : '#f9fafb',
                                    border: `1px solid ${borderCol}`, borderRadius: 8, padding: '8px 12px',
                                }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        placeholder="Search by indent no., outlet, or material…"
                                        style={{
                                            border: 'none', background: 'transparent', flex: 1,
                                            outline: 'none', color: textMain, fontSize: 13, fontFamily: 'inherit',
                                        }}
                                    />
                                    {searchTerm && (
                                        <button onClick={() => setSearchTerm('')} style={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: textMuted, fontSize: 18, lineHeight: 1, padding: 0,
                                        }}>×</button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Second Row: Search */}
                        {/* <div>
                            <Form.Label style={{ 
                                color: textMuted, 
                                marginBottom: 6, 
                                fontSize: 12, 
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                Search
                            </Form.Label>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: isDark ? '#252d3d' : '#f9fafb',
                                border: `1px solid ${borderCol}`, borderRadius: 8, padding: '8px 12px',
                            }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                <input
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Search by indent no., outlet, or material…"
                                    style={{
                                        border: 'none', background: 'transparent', flex: 1,
                                        outline: 'none', color: textMain, fontSize: 13, fontFamily: 'inherit',
                                    }}
                                />
                                {searchTerm && (
                                    <button onClick={() => setSearchTerm('')} style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: textMuted, fontSize: 18, lineHeight: 1, padding: 0,
                                    }}>×</button>
                                )}
                            </div>
                        </div> */}
                    </div>

                    {/* ── Status Filter Chips ── */}
                    {!loading && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {[
                                    { key: 'ALL', label: 'All' },
                                    ...Object.keys(STATUS_CONFIG).map(k => ({ key: k, label: STATUS_CONFIG[k].label }))
                                ].map(({ key, label }) => {
                                    const count = key === 'ALL'
                                        ? getAllCount()
                                        : (statusCounts[key.toLowerCase()] || 0);
                                    const cfg = STATUS_CONFIG[key] || {};
                                    const active = statusFilter === key;
                                    return (
                                        <button key={key} onClick={() => handleStatusChange(key)} style={{
                                            border: `1.5px solid ${active ? (cfg.border || '#a5b4fc') : borderCol}`,
                                            background: active ? (cfg.bg || '#eef2ff') : cardBg,
                                            color: active ? (cfg.color || '#6366f1') : textMuted,
                                            borderRadius: 20, padding: '5px 14px', fontSize: 12,
                                            fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
                                            fontFamily: 'inherit',
                                        }}>
                                            {label} ({count})
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Combined PDF button */}
                            {(statusFilter === 'PENDING' || statusFilter === 'ACCEPTED') && stockRequests.length > 0 && (
                                <button
                                    onClick={() => handleDownloadCombinedPDF(stockRequests)}
                                    style={{
                                        background: '#fef3c7',
                                        border: '1.5px solid #fcd34d',
                                        borderRadius: 20,
                                        padding: '5px 14px',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#d97706',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        fontFamily: 'inherit',
                                        whiteSpace: 'nowrap',
                                        transition: 'background .15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}
                                    title="Download combined PDF of all Pending + Accepted requests"
                                >
                                    📄 Cumulative PDF
                                </button>
                            )}
                        </div>
                    )}

                    {/* ── Error ── */}
                    {error && (
                        <div style={{
                            background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 9,
                            padding: '11px 15px', marginBottom: 14, color: '#dc2626', fontSize: 13,
                        }}>{error}</div>
                    )}

                    {/* ── Loading Skeleton ── */}
                    {loading && (
                        <>
                            <style>{`@keyframes shimmer{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{
                                    background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10,
                                    padding: '16px 18px', marginBottom: 12,
                                    animation: 'shimmer 1.5s ease-in-out infinite',
                                }}>
                                    <div style={{ height: 14, width: '32%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5, marginBottom: 10 }} />
                                    <div style={{ height: 11, width: '20%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5 }} />
                                </div>
                            ))}
                        </>
                    )}

                    {/* ── Empty State ── */}
                    {!loading && !error && stockRequests.length === 0 && (
                        <div style={{
                            background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10,
                            padding: '52px 20px', textAlign: 'center',
                        }}>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: textMain, marginBottom: 5 }}>
                                No stock requests found
                            </div>
                            <div style={{ color: textMuted, fontSize: 13 }}>
                                {searchTerm || statusFilter !== 'ALL'
                                    ? 'Try adjusting your search or filters'
                                    : selectedOutlet?.value
                                        ? 'This outlet has no stock requests for the selected date range'
                                        : 'Stock requisitions will appear here once raised'}
                            </div>
                        </div>
                    )}

                    {/* ── Request Cards ── */}
                    {!loading && stockRequests
                        .filter(req => {
                            // Client-side search filter (for real-time search)
                            if (!searchTerm) return true;
                            const search = searchTerm.toLowerCase();
                            return (
                                req.indentNumber?.toLowerCase().includes(search) ||
                                req.outlet?.name?.toLowerCase().includes(search) ||
                                req.outlet?.outletName?.toLowerCase().includes(search) ||
                                req.items?.some(i => i.rawMaterial?.name?.toLowerCase().includes(search))
                            );
                        })
                        .map(req => (
                            <RequestCard
                                key={req._id}
                                req={req}
                                isDark={isDark}
                                onUpdate={(r) => handleUpdateClick(r)}
                                onDownloadPDF={handleDownloadPDF}
                            />
                        ))}

                    {/* ── Pagination ── */}
                    {!loading && stockRequests.length > 0 && totalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isDark={isDark}
                        />
                    )}

                    {/* ── Footer Info ── */}
                    {!loading && stockRequests.length > 0 && (
                        <div style={{
                            textAlign: 'center',
                            color: textMuted,
                            fontSize: 12,
                            marginTop: 16,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap'
                        }}>
                            <span>
                                Showing {((currentPage - 1) * 20) + 1} - {Math.min(currentPage * 20, totalRecords)} of {totalRecords} request{totalRecords !== 1 ? 's' : ''}
                            </span>
                            <span style={{ color: borderCol }}>•</span>
                            <span>
                                Page {currentPage} of {totalPages}
                            </span>
                        </div>
                    )}

                </Container>
            </div>
        </React.Fragment>
    );
};

export default StocksRequest;